import {
  IconButton,
  makeStyles,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@material-ui/core'
import { CSSProperties } from '@material-ui/core/styles/withStyles'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import { isEmpty, isEqual } from 'lodash'
import { useAppSelector } from 'store/configureStore'
import { WorkingStepType } from 'services/APIs/InternalAPI/internal-api.contracts'
import { useWorkingStepFilter } from '../BoMItemFilter/Components/WorkingStepsFilter/useWorkingStepFilter'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'
import { WorkingStepStats } from '../store/selectors/assemblyWorkingStepsSummaryWithCounterSelector'

type WorkingStepIconSize = 'small' | 'medium' | 'large'

type StyleProps = {
  size?: WorkingStepIconSize
}

type OwnProps = {
  // partTypeWorkingStepDtos: WorkingStepDto[]
  bomItemId: string
  includeAssemblyWorkingSteps?: boolean
  onClick?: (workingStepStats: WorkingStepStats) => void
  getButtonVariant?: (workingStep: WorkingStepType) => 'filled' | 'outlined'
  includeCountOfWorkingSteps?: boolean
  shouldFilter?: boolean
  ulClassName?: string
  onlyAssemblyWorkingSteps?: boolean
}

type Props = StyleProps & OwnProps

const getSize = (size?: WorkingStepIconSize) => {
  switch (size) {
    case 'small':
      return '24px'
    case 'medium':
      return '32px'
    case 'large':
      return '42px'
    default:
      return '24px'
  }
}

const useStyles = makeStyles<Theme, StyleProps>(() => ({
  workingStepSummaryList: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: '4px',
  },
  workingStepSummaryIcon: {
    '& svg': {
      width: (props) => getSize(props.size),
    },
  },
}))

export const WorkingStepsSummary = (
  props: {
    workingStepsStats: Partial<Record<WorkingStepType, WorkingStepStats>>
    notAllowedWorkingSteps?: WorkingStepType[]
  } & Omit<Props, 'bomItemId'>
) => {
  const cn = useStyles({ ...props })

  const theme = useTheme()

  const { getLocalizedWorkingStep } = useLocalizedWorkingStepLabel()

  const { handleChangeFilter, filterIsActive } = useWorkingStepFilter()

  return (
    <ul className={props.ulClassName || cn.workingStepSummaryList}>
      {Object.values(props.workingStepsStats)?.map((ws) => {
        const Icon = () => (
          <WorkingStepIconSvg
            disableTitle
            workingStepType={ws.primaryWorkingStep}
          />
        )

        const buttonStyles: CSSProperties = {}

        if (
          (props.getButtonVariant &&
            props.getButtonVariant(ws.primaryWorkingStep) === 'filled') ||
          (props.shouldFilter && filterIsActive(ws.primaryWorkingStep))
        ) {
          buttonStyles.color = theme.palette.primary.contrastText
          buttonStyles.backgroundColor = theme.palette.primary.main
        }

        const notAllowed = props.notAllowedWorkingSteps?.includes(
          ws.primaryWorkingStep
        )

        if (notAllowed) {
          ;(buttonStyles.color = theme.palette.error.contrastText),
            (buttonStyles.backgroundColor = theme.palette.error.main)
        }

        return (
          <li key={ws.primaryWorkingStep}>
            <Tooltip
              title={getLocalizedWorkingStep(ws.primaryWorkingStep)}
              placement="left"
              arrow
            >
              <div style={{ textAlign: 'center' }}>
                {props.includeCountOfWorkingSteps &&
                props.workingStepsStats &&
                props.workingStepsStats[ws.primaryWorkingStep] ? (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    component="p"
                  >
                    {props.workingStepsStats[ws.primaryWorkingStep].count}
                  </Typography>
                ) : null}
                {props.onClick || props.shouldFilter ? (
                  <IconButton
                    style={buttonStyles}
                    size="small"
                    disabled={notAllowed}
                    onClick={() => {
                      if (props.shouldFilter) {
                        handleChangeFilter(ws.primaryWorkingStep)
                      }
                      if (props.onClick) {
                        props.onClick(
                          props.workingStepsStats[ws.primaryWorkingStep]
                        )
                      }
                    }}
                    className={cn.workingStepSummaryIcon}
                  >
                    <Icon />
                  </IconButton>
                ) : (
                  <span className={cn.workingStepSummaryIcon}>
                    <Icon />
                  </span>
                )}
              </div>
            </Tooltip>
          </li>
        )
      })}
    </ul>
  )
}

export const ProjectPriceSummariesWorkingStepSummary = (
  props: Omit<Props, 'bomItemId'>
) => {
  const workingStepsStats = useAppSelector(
    projectSelectors.projectPriceSummariesWorkingStepSummary
  )

  if (isEmpty(workingStepsStats)) return null

  return (
    <WorkingStepsSummary {...props} workingStepsStats={workingStepsStats} />
  )
}

export const BomItemWorkingStepSummary = (props: Props) => {
  const workingStepsStats = useAppSelector(
    projectSelectors.assemblyWorkingStepsSummaryWithCounterSelector(
      props.bomItemId,
      props.onlyAssemblyWorkingSteps
    ),
    isEqual
  )

  const notAllowedWorkingSteps = useAppSelector(
    projectSelectors.notAllowedWorkingStepsSelector(props.bomItemId)
  )

  if (!workingStepsStats) {
    return null
  }

  return (
    <WorkingStepsSummary
      {...props}
      workingStepsStats={workingStepsStats}
      notAllowedWorkingSteps={notAllowedWorkingSteps}
    />
  )
}
